import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import capitalize from 'lodash/capitalize';
// components
import PageGrid from 'shared/grids/PageGrid';
import { BaseCell, DateCell } from 'shared/grids/Cells';
import {
  DateRangeFilterCell, FieldFilterCell, UpdatedByFilterCell, ValueFilterCell,
} from './controls';
// hooks
import useRequests from './useRequests';
import useFilters from './useFilters';
// helpers
import { longDateTimeFormatter } from 'shared/../constants/Formatting';
import { formatDateTime } from 'src/shared/helpers/date';
// style
import './style.scss';
import BaseButton from 'shared/buttons';
import { downloadFile } from 'shared/../helpers/downloadFile';
import {
  getAuditSummary,
} from 'src/fetch/orders';
import { AUDIT_TYPES } from './consts';
import { Label } from '@progress/kendo-react-labels';
import moment from 'moment';
import NestedEntitiesFilterCell from './controls/NestedEntitiesFilterCell';
import NestedEntityNamesFilterCell from './controls/NestedEntityNamesFilterCell';

const initialSort = [{ field: 'effectiveDate', dir: 'desc' }];

const formatSearchParams = ({
  effectiveDate, dateFrom, dateTo, ...others
}) => ({
  dateFrom: effectiveDate?.start,
  dateTo: effectiveDate?.end,
  ...others,
});

const AuditHomePage = ({
  id,
  type,
  variant,
  hiddenFilterFields,
}) => {
  const [auditSummary, setAuditSummary] = useState();
  const [selectedNestedEntity, setSelectedNestedEntity] = useState();

  const { getData, getDataExcel } = useRequests(type, id, variant);
  const {
    searchParams, filter, fields, users, filterChange, nestedEntities, nestedEntityNames, shouldUseFilter,
  } = useFilters(type, id, variant, selectedNestedEntity);

  const [currentFilter, setCurrentFilter] = useState({});
  const [currentTotal, setCurrentTotal] = useState(0);
  const getUtcData = (params) => {
    setCurrentFilter(params);

    return getData(params).then(({ data, ...other }) => {
      setCurrentTotal(other.total);

      return {
        data: data.map(({ effectiveDate, ...item }) => ({
          ...item,
          effectiveDate: effectiveDate.endsWith('Z') ? effectiveDate : `${effectiveDate}Z`,
        })),
        ...other,
      };
    });
  };

  const handleNestedEntityChange = ({ value }) => {
    setSelectedNestedEntity(value);
  };

  const filterFields = useMemo(() => {
    const originalFilterFields = [
      { name: 'propertyName', label: 'Field', Component: FieldFilterCell(fields || []) },
      { name: 'updatedBy', label: 'Updated By', Component: UpdatedByFilterCell(users || []) },
      { name: 'nestedEntity', label: 'Nested Entity Type', Component: NestedEntitiesFilterCell({ nestedEntities: nestedEntities || [], onChange: handleNestedEntityChange }) },
      ...(selectedNestedEntity ? [{ name: 'nestedEntityName', label: 'Nested Entity Name', Component: NestedEntityNamesFilterCell(nestedEntityNames || []) }] : []),
      { name: 'before', label: 'Before', Component: ValueFilterCell },
      { name: 'after', label: 'After', Component: ValueFilterCell },
      { name: 'effectiveDate', label: 'Effective Date', Component: DateRangeFilterCell },
    ];

    if (hiddenFilterFields?.length > 0) {
      return originalFilterFields.filter((f) => !hiddenFilterFields.includes(f.name));
    }
    return originalFilterFields;
  }, [nestedEntities, nestedEntityNames, selectedNestedEntity, fields, users, hiddenFilterFields]);

  const exportBackend = () => {
    const targetObject = {};

    Object.entries(currentFilter).forEach(([key, value]) => {
      if (!['pageSize', 'page'].includes(key)) {
        targetObject[key] = value;
      }
    });

    getDataExcel({ ...targetObject, pageSize: currentTotal }).then((data) => {
      downloadFile({
        data,
        name: `${type}_audit.xlsx`,
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
    });
  };

  const additionalButtons = () => <BaseButton onClick={exportBackend} text="Export" icon="export" />;

  useEffect(() => {
    if (id && type === AUDIT_TYPES.ORDER) {
      getAuditSummary(id).then((data) => {
        setAuditSummary(data);
      });
    }
  }, [id]);

  return (
    <>
      <Helmet title={`${capitalize(type.replace('-', ' '))} ${id} History | CMS | Gulf Winds`} />
      <div className='audits'>
        <PageGrid
          name='Audits'
          getData={getUtcData}
          useFilterCache
          hideActionsCell
          disableAdd
          initialSort={initialSort}
          hasUnifiedTextSearch={false}
          resizable
          hasExport={type !== 'quote'}
          filterFields={shouldUseFilter ? filterFields : []}
          formatSearchParams={formatSearchParams}
          onReset={() => setSelectedNestedEntity(null)}
          additionalButtons={type === 'quote' ? additionalButtons() : null}
          columns={[
            {
              title: 'Field',
              field: 'property',
            },
            {
              title: 'Display Name',
              field: 'displayName',
            },
            { title: 'Before', field: 'before' },
            { title: 'After', field: 'after' },
            {
              title: 'Effective Date',
              field: 'effectiveDate',

              cell: ({ dataItem: { effectiveDate }, ...props }) => (
                <DateCell date={effectiveDate} formatMethod={longDateTimeFormatter} {...props} />
              ),
            },
            { title: 'Updated By', field: 'updatedBy' },
          ]}
          filtersExtraEnd={
            auditSummary && (
              <div style={{
                flexBasis: '100%',
                marginTop: 10,
              }}
              >
                <Label style={{
                  fontWeight: 'bold',
                  fontSize: 16,
                  marginBottom: 5,
                }}
                >
                  Order Detail Summary
                </Label>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
                >
                  <div>
                    <b>Created By: </b>
                    {auditSummary.createdBy}
                  </div>
                  <div>
                    <b>Created Date/Time: </b>
                    {formatDateTime(auditSummary.createdOn)}
                  </div>
                  <div>
                    <b>Last Modified By: </b>
                    {auditSummary.updatedBy}
                  </div>
                  <div>
                    <b>Create Method: </b>
                    {auditSummary.creationMethod}
                  </div>
                  <div>
                    <b>Original Quote Lane: </b>
                    {auditSummary.originalQuoteLane}
                  </div>
                </div>
              </div>
            )
          }
        />
      </div>
    </>
  );
};

export default AuditHomePage;
